export const SPEC_IMPORT_PROMPT = {
  version: '1.0',
  lastUpdated: '2026-03-04',
  content: `아래 첨부된 마크다운 파일은 기능명세(Functional Specification) 문서입니다.
이 문서를 분석하여 MaioS 시스템에 import할 수 있는 JSON 형식으로 변환해주세요.

## 출력 형식

JSON 배열로 출력해주세요. 각 항목이 하나의 기능명세입니다.
문서에 여러 개의 기능이 포함되어 있으면 각각을 분리하여 배열 항목으로 만들어주세요.
하나의 기능만 있으면 1개짜리 배열로 출력해주세요.

\`\`\`json
[
  {
    "functionCode": "string (필수)",
    "title": "string (필수)",
    "actor": "string",
    "precondition": "string",
    "postcondition": "string",
    "mainFlow": [{ "step": 1, "description": "string" }],
    "alternativeFlow": [{ "step": "2a", "description": "string" }],
    "exceptionFlow": [{ "step": "E1", "description": "string" }],
    "inputSpec": [{ "fieldName": "string", "type": "string", "constraint": "string" }],
    "outputSpec": [{ "fieldName": "string", "type": "string", "constraint": "string" }],
    "businessRules": ["string"],
    "uiReference": "string",
    "detailSpec": [
      {
        "no": 1,
        "fieldName": "string",
        "type": "string",
        "required": true,
        "constraint": "string",
        "content": "string",
        "actionLogic": "string"
      }
    ],
    "status": "DRAFT"
  }
]
\`\`\`

## 필드별 매핑 가이드

### 필수 필드

1. **functionCode** — 기능 코드
   - 문서에 "기능 코드", "기능 ID", "FN-xxx", "기능번호" 등으로 표기된 값
   - 없으면 문서 제목이나 기능 이름에서 추출하여 "FN-001", "FN-002" 형태로 생성

2. **title** — 기능 명세 제목
   - 문서의 제목, 기능명, 유스케이스 이름 등

3. **mainFlow** — 주요 흐름 (메인 시나리오)
   - "기본 흐름", "Main Flow", "정상 시나리오", "주요 플로우", "사용자 시나리오", "기본 경로" 등으로 작성된 부분
   - 번호가 매겨진 단계별 절차를 step 배열로 변환
   - step 번호는 숫자(1, 2, 3) 또는 문자 포함(3a, 3b) 가능

### 선택 필드

4. **actor** — 수행 주체
   - "액터", "사용자", "Actor", "수행 주체" 등으로 표기된 값
   - 없으면 빈 문자열

5. **precondition** — 사전 조건
   - "사전 조건", "Precondition", "전제 조건", "선행 조건" 등으로 표기된 값

6. **postcondition** — 사후 조건
   - "사후 조건", "Postcondition", "결과", "완료 조건" 등으로 표기된 값

7. **alternativeFlow** — 대안 흐름
   - "대안 흐름", "Alternative Flow", "대체 시나리오", "변형 흐름" 등
   - step 번호에 알파벳 접미사를 사용 (예: "2a", "3b")

8. **exceptionFlow** — 예외 흐름
   - "예외 흐름", "Exception Flow", "에러 처리", "오류 시나리오" 등
   - step 번호에 "E" 접두사 사용 가능 (예: "E1", "E2")

9. **inputSpec** — 입력 필드 정의
   - "입력 데이터", "요청 파라미터", "Input", "입력 필드" 등으로 표기된 테이블
   - 각 행을 { fieldName, type, constraint } 객체로 변환

10. **outputSpec** — 출력 필드 정의
    - "출력 데이터", "응답 데이터", "Output", "반환 값" 등으로 표기된 테이블

11. **businessRules** — 비즈니스 규칙
    - "비즈니스 규칙", "업무 규칙", "Business Rules", "제약 사항" 등
    - 각 규칙을 문자열 배열로 변환

12. **uiReference** — UI 설계 참조
    - "UI 참조", "화면 설계", "피그마 링크", "디자인 참조" 등의 URL이나 텍스트

13. **detailSpec** — 상세 명세 (7컬럼 테이블)
    - "상세 명세", "화면 상세", "UI 명세", "기능 상세" 등으로 표기된 테이블
    - 7개 컬럼으로 매핑:
      - **no**: 번호 (자동 부여 가능)
      - **fieldName**: 기능명/항목명/필드명
      - **type**: 기능 타입 (Button, Input-box, Modal, Bottomsheet, Accordion, Dropdown, Checkbox, Radio, Toggle, Text, Link, Tab, Tooltip 등)
      - **required**: 필수 여부 (true/false)
      - **constraint**: 노출 조건/표시 조건
      - **content**: 내용/설명
      - **actionLogic**: 클릭/입력 시 동작 로직

14. **status** — 상태
    - 기본값: "DRAFT"
    - 문서에 명시되어 있으면 해당 값 사용
    - DRAFT, IN_REVIEW, APPROVED, IMPLEMENTED 중 하나

## 변환 규칙

1. 문서에 여러 기능이 포함된 경우 (## 또는 ### 헤딩으로 구분), 각각을 별도 JSON 객체로 분리
2. 흐름(Flow)의 각 단계는 반드시 { "step": 번호, "description": "설명" } 형태로 변환
3. 테이블 형태의 데이터(inputSpec, outputSpec, detailSpec)는 각 행을 객체로 변환
4. 마크다운의 체크리스트, 불릿 리스트도 흐름 단계로 변환 가능
5. 값이 없거나 해당 섹션이 문서에 없으면 해당 필드를 생략하거나 빈 값으로 설정
6. 원문의 내용을 가공하지 말고, 원문 그대로 해당 필드에 배치

## 변환 예시

### 입력 (마크다운)

\`\`\`markdown
# 로그인 기능 명세

- 기능 코드: FN-AUTH-001
- 작성일: 2026-01-15

## 개요
사용자가 이메일과 비밀번호로 시스템에 로그인하는 기능

## 액터
로그인하지 않은 사용자

## 사전 조건
- 사용자가 회원가입을 완료한 상태

## 주요 흐름
1. 사용자가 로그인 페이지에 접근한다.
2. 이메일 주소를 입력한다.
3. 비밀번호를 입력한다.
4. "로그인" 버튼을 클릭한다.
5. 시스템이 자격 증명을 검증한다.
6. 대시보드 페이지로 이동한다.

## 대안 흐름
2a. 소셜 로그인 버튼(Google)을 클릭한다.
2b. Google OAuth 인증을 수행한다.
2c. 인증 완료 후 대시보드로 이동한다.

## 예외 흐름
E1. 이메일 또는 비밀번호가 일치하지 않으면 "로그인 정보를 확인해주세요" 오류 메시지를 표시한다.
E2. 5회 이상 로그인 실패 시 계정을 잠금 처리한다.

## 입력 데이터
| 필드명 | 타입 | 제약조건 |
|--------|------|----------|
| email | string | 이메일 형식, 필수 |
| password | string | 8자 이상, 필수 |

## 비즈니스 규칙
- 비밀번호는 8자 이상, 영문+숫자+특수문자 조합
- 로그인 실패 5회 시 30분간 계정 잠금
- JWT 토큰 유효기간은 24시간

## 화면 상세
| 번호 | 기능명 | 타입 | 필수 | 노출 조건 | 내용 | 동작 로직 |
|------|--------|------|------|-----------|------|-----------|
| 1 | 이메일 입력 | Input-box | Y | 항상 | 이메일 주소 입력 필드 | 이메일 형식 실시간 검증 |
| 2 | 비밀번호 입력 | Input-box | Y | 항상 | 비밀번호 입력 필드 (마스킹) | 최소 8자 검증 |
| 3 | 로그인 버튼 | Button | Y | 이메일/비밀번호 입력 시 | 로그인 실행 | API 호출 후 대시보드 이동 |
| 4 | Google 로그인 | Button | N | 항상 | Google OAuth 로그인 | Google OAuth 팝업 실행 |
| 5 | 비밀번호 찾기 | Link | N | 항상 | 비밀번호 재설정 페이지 이동 | /forgot-password로 이동 |
\`\`\`

### 출력 (JSON)

\`\`\`json
[
  {
    "functionCode": "FN-AUTH-001",
    "title": "로그인 기능 명세",
    "actor": "로그인하지 않은 사용자",
    "precondition": "사용자가 회원가입을 완료한 상태",
    "postcondition": "",
    "mainFlow": [
      { "step": 1, "description": "사용자가 로그인 페이지에 접근한다." },
      { "step": 2, "description": "이메일 주소를 입력한다." },
      { "step": 3, "description": "비밀번호를 입력한다." },
      { "step": 4, "description": "\\"로그인\\" 버튼을 클릭한다." },
      { "step": 5, "description": "시스템이 자격 증명을 검증한다." },
      { "step": 6, "description": "대시보드 페이지로 이동한다." }
    ],
    "alternativeFlow": [
      { "step": "2a", "description": "소셜 로그인 버튼(Google)을 클릭한다." },
      { "step": "2b", "description": "Google OAuth 인증을 수행한다." },
      { "step": "2c", "description": "인증 완료 후 대시보드로 이동한다." }
    ],
    "exceptionFlow": [
      { "step": "E1", "description": "이메일 또는 비밀번호가 일치하지 않으면 \\"로그인 정보를 확인해주세요\\" 오류 메시지를 표시한다." },
      { "step": "E2", "description": "5회 이상 로그인 실패 시 계정을 잠금 처리한다." }
    ],
    "inputSpec": [
      { "fieldName": "email", "type": "string", "constraint": "이메일 형식, 필수" },
      { "fieldName": "password", "type": "string", "constraint": "8자 이상, 필수" }
    ],
    "outputSpec": [],
    "businessRules": [
      "비밀번호는 8자 이상, 영문+숫자+특수문자 조합",
      "로그인 실패 5회 시 30분간 계정 잠금",
      "JWT 토큰 유효기간은 24시간"
    ],
    "uiReference": "",
    "detailSpec": [
      { "no": 1, "fieldName": "이메일 입력", "type": "Input-box", "required": true, "constraint": "항상", "content": "이메일 주소 입력 필드", "actionLogic": "이메일 형식 실시간 검증" },
      { "no": 2, "fieldName": "비밀번호 입력", "type": "Input-box", "required": true, "constraint": "항상", "content": "비밀번호 입력 필드 (마스킹)", "actionLogic": "최소 8자 검증" },
      { "no": 3, "fieldName": "로그인 버튼", "type": "Button", "required": true, "constraint": "이메일/비밀번호 입력 시", "content": "로그인 실행", "actionLogic": "API 호출 후 대시보드 이동" },
      { "no": 4, "fieldName": "Google 로그인", "type": "Button", "required": false, "constraint": "항상", "content": "Google OAuth 로그인", "actionLogic": "Google OAuth 팝업 실행" },
      { "no": 5, "fieldName": "비밀번호 찾기", "type": "Link", "required": false, "constraint": "항상", "content": "비밀번호 재설정 페이지 이동", "actionLogic": "/forgot-password로 이동" }
    ],
    "status": "DRAFT"
  }
]
\`\`\`

## 주의사항

- JSON만 출력해주세요. 설명이나 코멘트는 JSON 밖에 작성해주세요.
- 문서의 원문을 그대로 보존해주세요. 내용을 요약하거나 생략하지 마세요.
- 문서에 없는 필드는 빈 문자열(""), 빈 배열([]), 또는 false로 설정해주세요.
- functionCode가 문서에 명시되어 있지 않으면 기능 이름에서 유추하여 "FN-001", "FN-002" 형태로 생성해주세요.
- detailSpec 테이블의 "필수" 열에 "Y", "O", "필수" 등이 있으면 true로, 그 외는 false로 변환해주세요.
- 하나의 문서에 여러 기능이 포함된 경우 각각을 별도 JSON 객체로 분리해주세요. 기능의 경계는 보통 ## 또는 ### 레벨의 헤딩으로 구분됩니다.`,
};
